import { useState, useCallback, useMemo, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import NumberBall from '@/components/base/NumberBall';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useLottoData } from '@/hooks/useLottoData';
import { validateOrFallback, validateLottoCombination, generateSafeRandomCombination } from '@/lib/lottoCombination';
import {
  generateBalanced,
  generateEndDigit,
  generateHotCold,
  generateRecentCold,
  generateWeighted,
} from '@/lib/numberGenerators';
import QRShare from '@/components/feature/QRShare';
import Reveal from '@/components/base/Reveal';
import ResponsibleNotice from '@/components/feature/ResponsibleNotice';

// ---- Strategy types ----
type StrategyKey = 'weighted' | 'balanced' | 'hotcold' | 'recentcold' | 'enddigit';

interface Strategy {
  key: StrategyKey;
  label: string;
  icon: string;
  description: string;
  detail: string;
}

const strategies: Strategy[] = [
  {
    key: 'weighted',
    label: '가중치 랜덤',
    icon: 'ri-scales-3-line',
    description: '출현 빈도가 높은 번호일수록 더 높은 확률로 선택됩니다.',
    detail: '통계적으로 자주 등장한 번호에 가중치를 부여해, 과거 데이터의 흐름을 반영한 번호를 추천합니다.',
  },
  {
    key: 'balanced',
    label: '밸런스 생성',
    icon: 'ri-equalizer-line',
    description: '홀짝 3:3, 번호대 분산, 총합 100~175 범위로 균형 잡힌 조합.',
    detail: '실제 당첨 패턴과 가장 유사한 조건(홀짝 균형, 번호대 분산, 적정 총합)을 모두 만족하는 번호를 생성합니다.',
  },
  {
    key: 'hotcold',
    label: '핫-콜드 믹스',
    icon: 'ri-contrast-line',
    description: '핫 넘버 3개 + 콜드 넘버 3개를 조합한 믹스 전략.',
    detail: '출현이 많은 번호와 적은 번호를 반반 섞어 조합합니다. 어느 쪽에 치우치지 않는 다양한 조합을 만듭니다.',
  },
  {
    key: 'recentcold',
    label: '최근 미출현',
    icon: 'ri-time-line',
    description: '최근 10회차 동안 나오지 않은 번호에 더 높은 확률을 부여합니다.',
    detail: '최근 회차에 등장하지 않은 번호를 우선해 조합합니다. (과거 미출현이 향후 당첨 확률을 높인다는 의미는 아닙니다.)',
  },
  {
    key: 'enddigit',
    label: '끝수 분산',
    icon: 'ri-numbers-line',
    description: '같은 끝자리 숫자가 2개를 넘지 않도록 제한하여 다양성을 높입니다.',
    detail: '끝자리가 같은 번호(예: 3, 13, 23, 33, 43)는 최대 2개까지만 포함. 더 다양한 번호대를 커버합니다.',
  },
];

// ---- Component ----

const SmartRecommender: FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { frequencyData, hotNumbers, coldNumbers, draws, loading, error, incomplete, loadedCount, retry } = useLottoData();

  const [activeStrategy, setActiveStrategy] = useState<StrategyKey>('weighted');
  const [showResult, setShowResult] = useState(false);
  const [numbers, setNumbers] = useState<number[]>([]);
  const [bonus, setBonus] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [saveError, setSaveError] = useState('');
  const [qrModal, setQrModal] = useState(false);
  const [fallbackNotice, setFallbackNotice] = useState('');

  const activeStrat = useMemo(() => strategies.find((s) => s.key === activeStrategy)!, [activeStrategy]);

  const generate = useCallback(() => {
    if (loading) return;
    setIsGenerating(true);
    setShowResult(false);
    setNumbers([]);
    setBonus(null);
    setSaved(false);
    setSaveLabel('');
    setFallbackNotice('');

    setTimeout(() => {
      const needsPastData =
        activeStrategy === 'weighted' ||
        activeStrategy === 'hotcold' ||
        activeStrategy === 'recentcold';
      const hasData = !loading && !error && loadedCount > 0;

      let result: { numbers: number[]; bonus: number };
      let usedFallback = false;

      // 과거 데이터가 필요한 전략인데 데이터가 없으면 통계 전략을 실행하지 않고
      // 안전한 랜덤 fallback 으로 대체한다(통계 기반 결과와 fallback 결과를 구분).
      if (needsPastData && !hasData) {
        result = generateSafeRandomCombination();
        usedFallback = true;
      } else {
        switch (activeStrategy) {
          case 'weighted':
            result = generateWeighted(frequencyData);
            break;
          case 'balanced':
            result = generateBalanced();
            break;
          case 'hotcold':
            result = generateHotCold(hotNumbers, coldNumbers);
            break;
          case 'recentcold':
            result = generateRecentCold(frequencyData, draws);
            break;
          case 'enddigit':
            result = generateEndDigit();
            break;
          default:
            result = generateWeighted(frequencyData);
        }
      }

      // 공통 validator 로 최종 결과를 검증(유효하지 않으면 안전한 랜덤으로 대체)
      const validated = validateOrFallback(result.numbers, result.bonus);

      setNumbers(validated.numbers);
      setBonus(validated.bonus);
      if (usedFallback) {
        setFallbackNotice('통계 데이터를 불러오지 못해 일반 랜덤으로 생성했습니다.');
      }
      setIsGenerating(false);
      setShowResult(true);
    }, 500);
  }, [activeStrategy, frequencyData, hotNumbers, coldNumbers, draws, loading, error, loadedCount]);

  const handleSave = async () => {
    if (saved || numbers.length !== 6 || bonus === null) return;
    if (!user) { navigate('/login'); return; }

    setSaving(true);
    setSaveError('');
    try {
      // 저장 직전에는 fail-closed: validator 통과 시에만 DB 저장한다.
      // 유효하지 않으면 화면 번호를 다른 랜덤 번호로 교체하지 않고 insert 도 하지 않는다.
      const validation = validateLottoCombination(numbers, bonus);
      if (!validation.valid) {
        setSaveError('생성된 번호가 유효하지 않습니다. 번호를 다시 생성해주세요.');
        return;
      }
      const validated = validation.combination; // 검증 통과 시 반환된 정렬된 조합 저장
      // 주 데이터 저장: 실제 생성 행을 확인해 실패를 성공으로 표시하지 않는다
      const { data: inserted, error: insertError } = await supabase
        .from('saved_numbers')
        .insert({
          user_id: user.id,
          numbers: validated.numbers,
          bonus: validated.bonus,
          generator_type: 'smart',
          label: saveLabel.trim() || null,
        })
        .select('id')
        .single();

      if (insertError || !inserted) {
        if (import.meta.env.DEV) {
          console.error('[SmartRecommender] 번호 저장 실패:', insertError?.message ?? 'no row');
        }
        setSaveError('번호를 저장하지 못했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      // 주 데이터 저장 성공 → 성공 UI 표시
      setSaved(true);

      // 보조 활동 로그: 번호·bonus 등 개인정보를 기록하지 않고 전략 이름만 남긴다
      try {
        const { error: logError } = await supabase.from('user_activity_logs').insert({
          user_id: user.id,
          action: 'generate_smart',
          detail: { strategy: activeStrategy },
        });
        if (logError && import.meta.env.DEV) {
          console.error('[SmartRecommender] 활동 로그 기록 실패:', logError.message);
        }
      } catch (logErr) {
        if (import.meta.env.DEV) {
          console.error('[SmartRecommender] 활동 로그 기록 예외:', logErr);
        }
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[SmartRecommender] 번호 저장 예외:', err);
      }
      setSaveError('번호를 저장하지 못했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="w-full">
      <Reveal className="w-full max-w-4xl mx-auto px-4 md:px-6 py-14 md:py-20 border-t border-background-200">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-3">
            스마트 추천 생성기
          </h2>
          <p className="text-foreground-500 text-base md:text-lg">
            통계 데이터를 기반으로 5가지 전략 중 하나를 선택해 번호를 추천받으세요
          </p>
        </div>

        {/* Strategy selector cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
          {strategies.map((strat) => (
            <button
              key={strat.key}
              onClick={() => { setActiveStrategy(strat.key); setShowResult(false); }}
              className={`
                relative p-4 rounded-2xl text-left transition-all duration-200 cursor-pointer
                ${activeStrategy === strat.key
                  ? 'bg-primary-50 border-2 border-primary-400'
                  : 'bg-background-100 border-2 border-transparent hover:bg-background-200 hover:border-background-300/50'
                }
              `}
            >
              <div className={`
                w-9 h-9 rounded-xl flex items-center justify-center mb-2.5 transition-colors
                ${activeStrategy === strat.key ? 'bg-primary-500 text-white' : 'bg-background-200 text-foreground-500'}
              `}>
                <i className={`${strat.icon} text-base`}></i>
              </div>
              <p className={`text-sm font-bold mb-1 ${activeStrategy === strat.key ? 'text-primary-700' : 'text-foreground-800'}`}>
                {strat.label}
              </p>
              <p className="text-xs text-foreground-400 leading-relaxed line-clamp-3">
                {strat.description}
              </p>
            </button>
          ))}
        </div>

        {/* 데이터 상태 안내 (로드 실패/불완전) */}
        {error ? (
          <div className="mb-6 p-4 rounded-xl border border-accent-200 bg-accent-50 flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-sm text-foreground-700 flex items-center gap-2">
              <i className="ri-error-warning-line text-accent-600"></i>
              통계 데이터를 불러오지 못했습니다.
            </span>
            <button
              onClick={retry}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              다시 시도
            </button>
          </div>
        ) : incomplete ? (
          <div className="mb-6 p-4 rounded-xl border border-secondary-200 bg-secondary-50">
            <span className="text-sm text-foreground-700 flex items-center gap-2">
              <i className="ri-information-line text-secondary-600"></i>
              일부 통계 데이터만 로드되어 추천 결과가 참고용일 수 있습니다.
            </span>
          </div>
        ) : null}

        {/* Active strategy detail */}
        <div className="bg-background-100 border border-background-200 rounded-2xl p-6 md:p-8 mb-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-accent-100 flex items-center justify-center flex-shrink-0">
              <i className={`${activeStrat.icon} text-xl text-accent-600`}></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground-900 mb-1.5">{activeStrat.label} 전략</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">{activeStrat.detail}</p>
            </div>
          </div>

          {/* Number display area */}
          <div className="min-h-[160px] flex flex-col items-center justify-center">
            {!showResult && !isGenerating && !loading && (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-brain-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-400 text-sm">아래 버튼을 눌러 번호를 추천받아보세요</p>
              </div>
            )}

            {loading && (
              <div className="flex items-center gap-2 text-foreground-400">
                <i className="ri-loader-4-line animate-spin"></i>
                <span className="text-sm">데이터 로딩 중...</span>
              </div>
            )}

            {isGenerating && (
              <div className="flex flex-col items-center gap-4">
                <div className="flex gap-2.5">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-12 h-12 rounded-full bg-background-300 animate-pulse"
                      style={{ animationDelay: `${i * 70}ms` }} />
                  ))}
                </div>
                <p className="text-foreground-500 text-sm">분석 중...</p>
              </div>
            )}

            {showResult && numbers.length === 6 && bonus !== null && (
              <div className="flex flex-col items-center gap-5">
                <div className="flex flex-wrap items-center justify-center gap-2.5">
                  {numbers.map((num, idx) => (
                    <NumberBall key={num} number={num} size="md" animated animationDelay={idx * 80} />
                  ))}
                  <div className="flex items-center justify-center w-7 h-12">
                    <span className="text-foreground-400 text-lg font-bold">+</span>
                  </div>
                  <NumberBall number={bonus} isBonus size="md" animated animationDelay={480} />
                </div>

                {fallbackNotice && (
                  <p className="text-xs text-accent-700 flex items-center gap-1.5">
                    <i className="ri-information-line"></i>
                    {fallbackNotice}
                  </p>
                )}

                {/* Save */}
                <div className="flex flex-col items-center gap-3 mt-2 pt-4 border-t border-background-200 w-full max-w-sm">
                  {saved ? (
                    <div className="flex items-center gap-2 text-secondary-600">
                      <i className="ri-check-double-line"></i>
                      <span className="text-sm font-medium">저장 완료!</span>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={saveLabel}
                        onChange={(e) => setSaveLabel(e.target.value)}
                        placeholder="라벨 (선택사항)"
                        className="w-full px-4 py-2 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary-600 hover:bg-secondary-700 text-white font-medium text-sm transition-all duration-200 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-bookmark-line"></i>
                          {saving ? '저장 중...' : '저장하기'}
                        </button>
                        <button
                          onClick={() => setQrModal(true)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-300 text-foreground-600 hover:bg-background-100 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-qr-code-line"></i>
                          QR 공유
                        </button>
                      </div>
                      {saveError && (
                        <p className="text-xs text-accent-600">{saveError}</p>
                      )}
                      {!user && (
                        <p className="text-xs text-foreground-400">
                          저장하려면 <button onClick={() => navigate('/login')} className="text-primary-600 underline cursor-pointer">로그인</button>이 필요합니다.
                        </p>
                      )}
                    </>
                  )}
                </div>

                {qrModal && <QRShare numbers={numbers} bonus={bonus} onClose={() => setQrModal(false)} />}
              </div>
            )}
          </div>
        </div>
      </Reveal>

      {/* Generate button */}
      <Reveal className="relative z-10 text-center" delay={150}>
        <button
          onClick={generate}
          disabled={isGenerating || loading}
          className="
            inline-flex items-center gap-2 px-8 py-3.5 rounded-full
            bg-accent-500 hover:bg-accent-600
            text-accent-950 font-bold text-base
            transition-all duration-200
            disabled:opacity-50 disabled:cursor-not-allowed
            whitespace-nowrap cursor-pointer
          "
        >
          <i className="ri-flashlight-line text-lg"></i>
          {showResult ? '다시 추천받기' : '스마트 추천받기'}
        </button>
      </Reveal>

      <ResponsibleNotice />
    </section>
  );
};

export default SmartRecommender;